import { formatMoney, offerChain, type Offer, type OfferCategory, type OfferMatch, type Store, type TripItemDeal } from '@sp/shared'
import type { IconName } from '../components/Icon.tsx'

const NBSP = ' '

export const CATEGORY_ICONS: Record<OfferCategory, IconName> = {
  'ovoce-zelenina': 'cat_produce',
  'maso-ryby': 'cat_meat',
  'mlecne-vejce': 'cat_dairy',
  pecivo: 'cat_bakery',
  trvanlive: 'cat_pantry',
  'sladke-slane': 'cat_sweets',
  mrazene: 'cat_frozen',
  napoje: 'cat_drinks',
  alkohol: 'cat_alcohol',
  drogerie: 'cat_drugstore',
  domacnost: 'cat_home',
  zvirata: 'cat_pets',
  'naradi-zahrada': 'cat_tools',
  elektronika: 'cat_electronics',
  obleceni: 'cat_clothes',
  ostatni: 'cat_other',
}

/** Akce jeste plati (nebo teprve zacne). */
export function offerIsCurrent(offer: Offer, now = Date.now()): boolean {
  return !offer.validTo || Date.parse(offer.validTo) >= now
}

export function offerPrice(offer: Offer): string | null {
  return offer.priceMinor === null ? null : formatMoney(offer.priceMinor, offer.currency)
}

function shortDay(iso: string): string {
  const d = new Date(iso)
  const weekday = d.toLocaleDateString('cs-CZ', { weekday: 'short' })
  return `${weekday} ${d.getDate()}.${NBSP}${d.getMonth() + 1}.`
}

/** „od po 14. 9.", „do st 16. 9.", „jen dnes". */
export function offerValidity(offer: Offer, now = new Date()): string | null {
  const from = offer.validFrom ? new Date(offer.validFrom) : null
  const to = offer.validTo ? new Date(offer.validTo) : null
  if (from && from.getTime() > now.getTime()) return `od ${shortDay(from.toISOString())}`
  if (!to) return null
  if (to.toDateString() === now.toDateString()) return 'jen dnes'
  return `do ${shortDay(to.toISOString())}`
}

/** Cena polozky z akce: „z akce do ne 20. 9. · s kartou". */
export function dealNote(deal: TripItemDeal, now = new Date()): string {
  let head = 'z akce'
  if (deal.validTo) {
    const to = new Date(deal.validTo)
    head = to.toDateString() === now.toDateString() ? 'z akce jen dnes' : `z akce do ${shortDay(deal.validTo)}`
  }
  return deal.requiresCard ? `${head} · s kartou` : head
}

/** „Akce končí ne 20. 9." — akce skonci pred dnem nakupu. */
export function dealEndsNote(deal: TripItemDeal): string {
  return deal.validTo ? `Akce končí ${shortDay(deal.validTo)}, před nákupem` : 'Akce skončí před nákupem'
}

/** Nazev pro lidi: cesky preklad, jinak nazev z letaku. */
export function offerTitle(offer: Offer): string {
  return offer.nameCs ?? offer.name
}

/** Baleni a cena za jednotku — bez nich nejde rozlisit 500 g a 1 kg teze akce. */
export function offerPack(offer: Offer): string | null {
  return [offer.description, offer.unitPrice].filter(Boolean).join(' · ') || null
}

/** Nazev z letaku, kdyz se ukazuje preklad — podle nej se v regalu hleda. */
export function offerOriginal(offer: Offer): string | null {
  return offer.nameCs && offer.nameCs !== offer.name ? offer.name : null
}

/** Akce jen z jedne verze letaku (Biedronka s pultem s obsluhou a bez nej). */
export function variantNote(offer: Offer): string | null {
  if (offer.variant === 'counter') return 'Jen v obchodech s pultem s obsluhou.'
  if (offer.variant === 'no_counter') return 'Jen v obchodech bez pultu s obsluhou.'
  if (offer.variant === 'supermarket') return 'Z letáku pro supermarkety.'
  if (offer.variant === 'hypermarket') return 'Z letáku pro hypermarkety.'
  return null
}

export function chainLabel(chainId: string): string {
  return offerChain(chainId)?.label ?? chainId
}

/** Akce polozek sablony podle id, jen platne. */
export function matchesByItem(matches: readonly OfferMatch[], now = Date.now()): Map<string, Offer[]> {
  const map = new Map<string, Offer[]>()
  for (const match of matches) {
    const current = match.offers.filter((offer) => offerIsCurrent(offer, now))
    if (current.length > 0) map.set(match.catalogItemId, current)
  }
  return map
}

/**
 * Akce polozky, ktere plati v jejim obchode. Akce chodi ze vsech obchodu skupiny;
 * u mleka v Biedronce nema svitit cena z Kauflandu. Obchod bez letaku nema zadne,
 * polozka bez obchodu (bezny nakup kdekoli) vsechny.
 */
export function offersForStore(offers: readonly Offer[] | undefined, store: Pick<Store, 'offerChain'> | null): Offer[] {
  if (!offers) return []
  if (!store) return [...offers]
  return store.offerChain ? offers.filter((offer) => offer.chain === store.offerChain) : []
}

/** Biedronka inzeruje hodne cen jen s kartou nebo pri vice kusech; Blix podminky neuvadi, jsou jen v obrazku. */
export function cardNote(offer: Offer): string | null {
  if (offer.requiresCard) return 'Cena platí jen s věrnostní kartou obchodu.'
  if (offer.chain === 'biedronka-pl') {
    return 'Ceny Biedronky často platí jen s kartou Moja Biedronka nebo při koupi více kusů – podmínky jsou na obrázku z letáku.'
  }
  return null
}
